import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, PageHeader, StatusPill, type DataListColumn } from "@ozpanel/kit";
import {
  Button,
  ConfirmDialog,
  Panel,
  Section,
  Stat,
  StateView,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  formatBytes,
  useAuth,
  useToast,
} from "@ozpanel/ui";

// Mail operations: the node-level surface (status, queue, quarantine, sending limits, junk
// learning, outbound identity). Every shape below was read from a live node.

interface MailStatus {
  node: string;
  serving_mail: boolean;
  services: Record<string, string> | null;
  // null = could not be read (the reason is under unread) — never shown as 0.
  counts: { domains?: number | null; mailboxes?: number | null; imap_sessions?: number | null } | null;
  unread?: Record<string, string>;
  queue: Record<string, number> | null;
}
// Every server's queue (mail/queuesnapshot.go): `node` is the server holding the message.
interface QueueItem {
  id?: string;
  queue?: string;
  sender?: string;
  recipients?: string[] | null;
  size_bytes?: number;
  reason?: string;
  node?: string;
}
// As mail_quarantine serialises it. This was declared with a singular `recipient` and a `score`,
// neither of which the server sends — it sends `recipients` (an ARRAY), `size_bytes` and `reason`,
// and arrived_at is a unix TIME, not a string. Two columns of a held-mail table were therefore
// always blank, on a screen whose entire job is to explain why a message did not arrive.
interface QuarantineItem {
  id?: string;
  sender?: string;
  recipients?: string[] | null;
  subject?: string;
  size_bytes?: number;
  arrived_at?: number;
  reason?: string;
  node?: string;
}
interface RateLimit {
  domain: string;
  rate_hourly: number;
  rate_daily: number;
}

// A chain of facts where EVERY link must hold for the feature to do anything. Rendering one
// "enabled" badge for a multi-part mechanism is how a setting reports itself active while
// doing nothing — the interlock rule. Each link is shown with its own state.
// ok null = NOT MEASURED (the server could not ask): neither a pass nor a failure.
function Chain({ links }: { links: { label: string; ok: boolean | null }[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {links.map((l) => (
        <StatusPill key={l.label} tone={l.ok == null ? "neutral" : l.ok ? "ok" : "bad"}>
          {l.ok == null ? "?" : l.ok ? "✓" : "✕"} {l.label}
        </StatusPill>
      ))}
    </div>
  );
}

function StatusTab() {
  const { client } = useAuth();
  const st = useQuery({
    queryKey: ["mail-status"],
    queryFn: () => client.apiFetch<MailStatus>("/mail/status"),
    refetchInterval: 20000,
  });
  const d = st.data;
  return (
    <Section title="Mail node" hint={d?.node}>
      <StateView loading={st.isLoading} error={st.error} errorContext="mail status">
        {d && (
          <>
            <div className="mb-4">
              <StatusPill tone={d.serving_mail ? "ok" : "bad"}>
                {d.serving_mail ? "serving mail" : "NOT serving mail"}
              </StatusPill>
            </div>
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Stat label="Mail domains" value={d.counts?.domains == null ? "not read" : String(d.counts.domains)} />
              <Stat label="Mailboxes" value={d.counts?.mailboxes == null ? "not read" : String(d.counts.mailboxes)} />
              <Stat label="IMAP sessions" value={d.counts?.imap_sessions == null ? "not read" : String(d.counts.imap_sessions)} />
            </div>
            {/* Mail is four daemons, not one. "postfix is active" says nothing about whether
                anything is being filtered, delivered or rate-limited, so each is named. */}
            <div className="text-[11.5px] uppercase tracking-wide text-muted">Services</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {Object.entries(d.services ?? {}).map(([name, state]) => (
                <StatusPill key={name} tone={state === "active" ? "ok" : "bad"}>
                  {name} {state}
                </StatusPill>
              ))}
            </div>
          </>
        )}
      </StateView>
    </Section>
  );
}

function QueueTab() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [confirmPurge, setConfirmPurge] = React.useState(false);

  const q = useQuery({
    queryKey: ["mail-queue"],
    queryFn: () =>
      client.apiFetch<{ items: QueueItem[] | null; total: number; oldest_seconds?: number; by_queue?: Record<string, number>; incomplete?: string }>(
        "/mail/queue",
      ),
    refetchInterval: 15000,
  });

  const act = useMutation({
    // The call is written where it is pressed, so its method and path are read there
    // (internal/console: a path handed to a wrapper is invisible to the reach check).
    mutationFn: (p: { call: () => Promise<unknown>; label: string }) => p.call().then(() => p.label),
    onSuccess: (label) => {
      notify({ kind: "success", title: String(label) });
      void qc.invalidateQueries({ queryKey: ["mail-queue"] });
    },
    onError: (e) => notify({ kind: "error", title: "Queue action failed", body: e instanceof Error ? e.message : String(e) }),
  });

  const columns: DataListColumn<QueueItem>[] = [
    { key: "id", label: "Queue ID", sortable: true, value: (m) => m.id || "" },
    { key: "node", label: "Server", sortable: true, value: (m) => m.node || "" },
    { key: "queue", label: "Queue", sortable: true, value: (m) => m.queue || "" },
    { key: "sender", label: "Sender", sortable: true, value: (m) => m.sender || "" },
    { key: "recipients", label: "Recipients", value: (m) => (m.recipients ?? []).join(", ") },
    { key: "size", label: "Size", sortable: true, value: (m) => m.size_bytes ?? 0, render: (m) => formatBytes(m.size_bytes) },
    { key: "reason", label: "Waiting because", value: (m) => m.reason || "" },
  ];

  const oldest = q.data?.oldest_seconds ?? 0;
  return (
    <Section
      title="Mail queue"
      hint={q.data ? `${q.data.total} message(s)` : undefined}
      actions={
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" disabled={act.isPending}
            onClick={() => act.mutate({ call: () => client.apiFetch("/mail/queue/flush", { method: "POST" }), label: "Queue flushed — delivery retried now" })}>
            Flush
          </Button>
          {(q.data?.total ?? 0) > 0 && (
            <Button size="sm" variant="danger" onClick={() => setConfirmPurge(true)}>
              Delete all
            </Button>
          )}
        </div>
      }
      bodyClassName="p-0"
    >
      <div className="p-4">
        {/* A queue is normal; a queue that is not draining is not. The oldest message says
            which of the two this is, and a total alone cannot. */}
        {oldest > 3600 && (
          <Panel className="mb-3 border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] p-3 text-[12.5px]">
            The oldest message has been queued for {Math.floor(oldest / 3600)}h. Mail that cannot be delivered is
            retried until it expires and then bounced — a queue this old usually means DNS, a firewall, or a
            receiving server refusing this node.
          </Panel>
        )}
        {q.data?.incomplete && (
          <Panel className="mb-3 border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] p-3 text-[12.5px]">{q.data.incomplete}</Panel>
        )}
        <DataList
          items={q.data?.items ?? []}
          columns={columns}
          getRowKey={(m) => String(m.id)}
          loading={q.isLoading}
          error={q.error}
          errorContext="the mail queue"
          emptyMessage="The queue is empty."
          emptyHint="Messages appear here only while they are waiting — an empty queue is the healthy state."
        />
      </div>
      <ConfirmDialog
        open={confirmPurge}
        onOpenChange={setConfirmPurge}
        title="Delete every queued message?"
        destructive
        confirmLabel="Delete all"
        pending={act.isPending}
        onConfirm={() => {
          setConfirmPurge(false);
          act.mutate({ call: () => client.apiFetch("/mail/queue/delete-all", { method: "POST" }), label: "Queue emptied" });
        }}
        body={
          <p>
            Every message waiting for delivery is destroyed, on every server, including ones that would have been
            delivered on the next retry. Held mail stays in the quarantine. Senders get no bounce — the mail simply never arrives. Flush first if you only want to
            retry delivery now.
          </p>
        }
      />
    </Section>
  );
}

function QuarantineTab() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["mail-quarantine"],
    queryFn: () =>
      client.apiFetch<{
        items: QuarantineItem[] | null;
        total: number;
        note?: string;
        incomplete?: string;
        wiring?: Record<string, unknown>;
      }>("/mail/quarantine"),
  });

  const w = (q.data?.wiring ?? {}) as Record<string, boolean | string | null>;
  const [acting, setActing] = React.useState<{ m: QuarantineItem; kind: "release" | "delete" } | null>(null);
  const run = (label: string, p: Promise<unknown>) =>
    p.then(() => { notify({ kind: "success", title: label }); void qc.invalidateQueries({ queryKey: ["mail-quarantine"] }); })
     .catch((e: unknown) => notify({ kind: "error", title: `${label} failed`, body: e instanceof Error ? e.message : String(e) }));

  const columns: DataListColumn<QuarantineItem>[] = [
    {
      key: "arrived_at", label: "Held", sortable: true, value: (m) => m.arrived_at ?? 0,
      render: (m) => (m.arrived_at ? new Date(m.arrived_at * 1000).toLocaleString() : "—"),
    },
    { key: "sender", label: "Sender", sortable: true, value: (m) => m.sender || "", render: (m) => <span className="font-mono text-[12.5px]">{m.sender || "—"}</span> },
    {
      key: "recipients", label: "To", value: (m) => (m.recipients ?? []).join(" "),
      render: (m) => <span className="font-mono text-[12.5px]">{(m.recipients ?? []).join(", ") || "—"}</span>,
    },
    { key: "subject", label: "Subject", value: (m) => m.subject || "", render: (m) => m.subject || <span className="text-muted">(no subject)</span> },
    { key: "node", label: "Server", sortable: true, value: (m) => m.node || "" },
    // WHY it was held. Without it the operator releasing a message cannot tell a false positive
    // from a real one, which is the whole judgement being asked of them.
    { key: "reason", label: "Why", value: (m) => m.reason || "", render: (m) => <span className="text-[12px] text-muted">{m.reason || "—"}</span> },
  ];

  return (
    <>
    <Section title="Quarantine" hint={q.data ? `${q.data.total} held` : undefined} bodyClassName="p-0">
      <div className="p-4">
        <StateView loading={q.isLoading} error={q.error} errorContext="the quarantine">
          <>
            {/* An EMPTY quarantine has two completely different meanings — nothing was spam, or
                nothing is being held — and they look identical. The wiring chain is what
                separates them, so it is shown above the (usually empty) list rather than
                hidden behind it. */}
            {q.data?.wiring && (
              <div className="mb-3">
                <div className="mb-1.5 text-[11.5px] uppercase tracking-wide text-muted">
                  Is anything actually being held?
                </div>
                <Chain
                  links={[
                    // Each link is what the server ASKED the daemon (null = could not ask). The
                    // file the panel writes is not a link: rspamd's resolved config is the answer.
                    { label: "rspamd adds the spam flag", ok: w.rspamd_live_use == null ? null : Boolean(w.rspamd_live_use) },
                    { label: "postfix holds a flagged message", ok: w.postfix_holds_on_flag == null ? null : Boolean(w.postfix_holds_on_flag) },
                  ]}
                />
                {typeof w.detail === "string" && (
                  <p className="mt-2 text-[11.5px] leading-relaxed text-muted">{w.detail}</p>
                )}
              </div>
            )}
            {q.data?.incomplete && (
              <Panel className="mb-3 border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] p-3 text-[12.5px]">{q.data.incomplete}</Panel>
            )}
            <DataList
              rowActions={(m: QuarantineItem) => (
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setActing({ m, kind: "release" })}>Release</Button>
                  <Button size="sm" variant="danger" onClick={() => setActing({ m, kind: "delete" })}>Delete</Button>
                </div>
              )}
              items={q.data?.items ?? []}
              columns={columns}
              getRowKey={(m) => String(m.id ?? `${m.sender}-${(m.recipients ?? []).join(",")}-${m.arrived_at}`)}
              emptyMessage={
                w.working ? "Nothing held — the quarantine is wired and no message has scored high enough." : "Nothing held."
              }
              emptyHint={
                w.working
                  ? undefined
                  : "One of the links above is broken, so an empty list does NOT mean there was no spam."
              }
            />
          </>
        </StateView>
      </div>
    </Section>

    <ConfirmDialog
      open={!!acting}
      onOpenChange={(o) => !o && setActing(null)}
      title={acting?.kind === "release" ? "Deliver this message?" : "Delete this message?"}
      confirmLabel={acting?.kind === "release" ? "Deliver it" : "Delete it"}
      destructive={acting?.kind === "delete"}
      body={
        acting?.kind === "release" ? (
          <p>
            It is delivered to {(acting?.m.recipients ?? []).join(", ") || "its recipients"} as it
            stands. Releasing does not teach the filter — if this sender keeps being held, add them
            to the domain's always-accept list.
          </p>
        ) : (
          <p>The message is discarded and the recipient never sees it. This cannot be undone.</p>
        )
      }
      onConfirm={() => {
        const a = acting;
        setActing(null);
        if (!a?.m.id) return;
        const id = encodeURIComponent(a.m.id);
        if (a.kind === "release") {
          void run("Message delivered", client.apiFetch(`/mail/quarantine/${id}/release`, { method: "POST" }));
        } else {
          void run("Message deleted", client.apiFetch(`/mail/quarantine/${id}`, { method: "DELETE" }));
        }
      }}
    />
    </>
  );
}

function LimitsTab() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();

  const rl = useQuery({
    queryKey: ["mail-ratelimits"],
    queryFn: () =>
      client.apiFetch<{
        limits: RateLimit[] | null;
        total: number;
        enforcing: boolean;
        note?: string;
      }>("/mail/ratelimits"),
  });

  const d = rl.data;
  const columns: DataListColumn<RateLimit>[] = [
    { key: "domain", label: "Domain", sortable: true },
    {
      key: "rate_hourly",
      label: "Per hour",
      sortable: true,
      value: (r) => r.rate_hourly ?? 0,
      render: (r) => (r.rate_hourly > 0 ? String(r.rate_hourly) : <span className="text-muted">unlimited</span>),
    },
    {
      key: "rate_daily",
      label: "Per day",
      sortable: true,
      value: (r) => r.rate_daily ?? 0,
      render: (r) => (r.rate_daily > 0 ? String(r.rate_daily) : <span className="text-muted">unlimited</span>),
    },
  ];

  return (
    <Section
      title="Outbound sending limits"
      hint={d ? "enforced by the gateway" : undefined}
      bodyClassName="p-0"
    >
      <div className="p-4">
        <StateView loading={rl.isLoading} error={rl.error} errorContext="the sending limits">
          {d && (
            <>
              <DataList
                items={d.limits ?? []}
                columns={columns}
                getRowKey={(r) => r.domain}
                emptyMessage="No domain has a sending limit."
                emptyHint="Limits come from each account's plan, overridden per domain where an operator has set one."
              />
              {d.note && <p className="mt-3 text-[11.5px] leading-relaxed text-muted">{d.note}</p>}
            </>
          )}
        </StateView>
      </div>
    </Section>
  );
}

export function MailOverviewScreen() {
  return (
    <div>
      <PageHeader title="Mail" subtitle="Node status, queue, quarantine and outbound limits" />
      <Tabs defaultValue="status">
        <TabsList>
          <TabsTrigger value="status">Status</TabsTrigger>
          <TabsTrigger value="queue">Queue</TabsTrigger>
          <TabsTrigger value="quarantine">Quarantine</TabsTrigger>
          <TabsTrigger value="limits">Sending limits</TabsTrigger>
        </TabsList>
        <TabsContent value="status">
          <StatusTab />
        </TabsContent>
        <TabsContent value="queue">
          <QueueTab />
        </TabsContent>
        <TabsContent value="quarantine">
          <QuarantineTab />
        </TabsContent>
        <TabsContent value="limits">
          <LimitsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
